import { useId, useState } from "react";
import { Link } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { ApiError } from "@/lib/api";
import { useAuth, type AuthUser } from "@/customer/auth/AuthContext";

// Inline account step. Signed-in customers see a compact "Signed in as
// <email>" affordance. Anonymous customers see a tabbed "I'm new" /
// "I have an account" form so signup or login can happen without
// leaving the checkout shell.
export function AccountSection({
  onAdvance
}: {
  onAdvance(): void;
}): JSX.Element {
  const { user } = useAuth();

  if (user) {
    return <SignedInPanel user={user} />;
  }
  return <AnonymousTabs onAuthed={onAdvance} />;
}

function SignedInPanel({ user }: { user: AuthUser }): JSX.Element {
  return (
    <div className="space-y-2">
      <p className="text-sm text-zinc-600">
        Signed in as{" "}
        <strong className="font-semibold text-brand-black">{user.email}</strong>.
      </p>
      <p className="text-xs text-zinc-500">
        Need a different account?{" "}
        <Link className="text-brand-red underline" to="/sign-in">
          Sign in with another email
        </Link>
        .
      </p>
    </div>
  );
}

function AnonymousTabs({ onAuthed }: { onAuthed(): void }): JSX.Element {
  return (
    <Tabs defaultValue="new">
      <TabsList aria-label="Account">
        <TabsTrigger value="new">I&rsquo;m new</TabsTrigger>
        <TabsTrigger value="returning">I have an account</TabsTrigger>
      </TabsList>
      <TabsContent value="new">
        <SignUpForm onAuthed={onAuthed} />
      </TabsContent>
      <TabsContent value="returning">
        <SignInForm onAuthed={onAuthed} />
      </TabsContent>
    </Tabs>
  );
}

function SignUpForm({ onAuthed }: { onAuthed(): void }): JSX.Element {
  const { signUp, loading } = useAuth();
  const ids = {
    first: useId(),
    last: useId(),
    phone: useId(),
    email: useId(),
    password: useId()
  };
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await signUp({
        email,
        password,
        password_confirmation: password,
        first_name: first,
        last_name: last,
        phone
      });
      onAuthed();
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div>
        <Label htmlFor={ids.first} className="mb-1 block text-sm">
          First name
        </Label>
        <Input id={ids.first} autoComplete="given-name" value={first} onChange={(e) => setFirst(e.target.value)} required />
      </div>
      <div>
        <Label htmlFor={ids.last} className="mb-1 block text-sm">
          Last name
        </Label>
        <Input id={ids.last} autoComplete="family-name" value={last} onChange={(e) => setLast(e.target.value)} required />
      </div>
      <div>
        <Label htmlFor={ids.phone} className="mb-1 block text-sm">
          Phone
        </Label>
        <Input id={ids.phone} type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
      </div>
      <div>
        <Label htmlFor={ids.email} className="mb-1 block text-sm">
          Email
        </Label>
        <Input id={ids.email} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor={ids.password} className="mb-1 block text-sm">
          Choose a password
        </Label>
        <Input
          id={ids.password}
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <p className="mt-1 text-xs text-zinc-500">At least 8 characters.</p>
      </div>
      {error ? (
        <Alert variant="destructive" className="md:col-span-2">
          {error}
        </Alert>
      ) : null}
      <div className="md:col-span-2 flex justify-end">
        <Button type="submit" disabled={loading}>
          {loading ? "Creating account…" : "Create account and continue"}
        </Button>
      </div>
    </form>
  );
}

function SignInForm({ onAuthed }: { onAuthed(): void }): JSX.Element {
  const { signIn, loading } = useAuth();
  const ids = { email: useId(), password: useId() };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await signIn(email, password);
      onAuthed();
    } catch (err) {
      setError(extractMessage(err));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <Label htmlFor={ids.email} className="mb-1 block text-sm">
          Email
        </Label>
        <Input id={ids.email} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      <div>
        <Label htmlFor={ids.password} className="mb-1 block text-sm">
          Password
        </Label>
        <Input id={ids.password} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <p className="mt-1 text-xs text-zinc-500">
          <Link className="text-brand-red underline" to="/password/reset">
            Forgot your password?
          </Link>
        </p>
      </div>
      {error ? <Alert variant="destructive">{error}</Alert> : null}
      <div className="flex justify-end">
        <Button type="submit" disabled={loading}>
          {loading ? "Signing in…" : "Sign in and continue"}
        </Button>
      </div>
    </form>
  );
}

function extractMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = err.message ? JSON.parse(err.message) : null;
      if (parsed && typeof parsed === "object") {
        const errs = (parsed as { errors?: Record<string, string[] | string> }).errors;
        if (errs) {
          const first = Object.values(errs)[0];
          if (Array.isArray(first)) return first[0] ?? "Something went wrong.";
          if (typeof first === "string") return first;
        }
        if (typeof (parsed as { error?: string }).error === "string") {
          return (parsed as { error: string }).error;
        }
      }
    } catch {
      // fall through to default
    }
    return err.message || "Something went wrong.";
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
